import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, RefreshCw, Plus, Trash2, BookOpen, Edit3, Eye, EyeOff, X, Sparkles, Send,
} from "lucide-react";

const CATEGORIES = ["contract", "script", "calculator", "checklist", "vendor", "video", "glossary"];
const TIER_GATES = ["free", "pro", "hustler", "team"];

const EMPTY = {
  slug: "", title: "", category: "script", summary: "", body_md: "",
  url: "", file_url: "", tier_gate: "free", tags: [], featured: false, published: true,
};

export default function AdminResources() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | EMPTY | existing doc

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/resources");
      setItems(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load resources");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const reseed = async () => {
    try {
      const r = await api.post("/admin/resources/seed");
      toast.success(`Seeded ${r.data.inserted} new, skipped ${r.data.skipped}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reseed failed");
    }
  };

  const sendHubBlast = async () => {
    if (!window.confirm("Send the Resources Hub email to ALL opted-in users right now?")) return;
    try {
      const r = await api.post("/admin/resources/hub/blast");
      toast.success(`Hub blast queued — sending to ${r.data.projected_recipients} opted-in users in the background.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Blast failed");
    }
  };

  const del = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/resources/${id}`);
      toast.success("Deleted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="admin-resources">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide flex items-center gap-1.5">
            <BookOpen size={12} /> Resources CMS
          </div>
          <div className="font-display font-extrabold text-2xl">Resource Library</div>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" className="btn btn-ghost" onClick={sendHubBlast} data-testid="admin-resources-hub-blast">
            <Send size={14} /> Send Hub blast
          </button>
          <button type="button" className="btn btn-ghost" onClick={reseed} data-testid="admin-resources-reseed">
            <Sparkles size={14} /> Re-run seed
          </button>
          <button type="button" className="btn btn-ghost" onClick={refresh} data-testid="admin-resources-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
          <button type="button" className="btn btn-primary" onClick={() => setEditing({ ...EMPTY })} data-testid="admin-resources-new">
            <Plus size={14} /> New resource
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card p-8 text-center text-[var(--ink-2)]">
          <Loader2 size={20} className="animate-spin inline" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="card p-12 text-center text-[var(--ink-2)]">
          <BookOpen size={32} className="inline mb-2 opacity-60" />
          <div className="font-semibold">No resources yet</div>
          <div className="text-xs mt-1">Click &quot;Re-run seed&quot; to populate, or &quot;New resource&quot; to create one.</div>
        </div>
      ) : (
        <div className="card p-2">
          <table className="w-full text-sm">
            <thead className="text-xs text-[var(--ink-2)] uppercase tracking-wide">
              <tr className="border-b">
                <th className="text-left p-2">Title</th>
                <th className="text-left p-2">Category</th>
                <th className="text-left p-2">Gate</th>
                <th className="text-right p-2">Views</th>
                <th className="text-center p-2">Status</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b last:border-b-0 hover:bg-slate-50" data-testid="admin-resource-row">
                  <td className="p-2">
                    <div className="font-semibold">{r.title}</div>
                    <div className="text-[10px] text-[var(--ink-3)] font-mono">{r.slug}</div>
                  </td>
                  <td className="p-2">
                    <span className="px-2 py-0.5 rounded text-[10px] bg-slate-100 text-slate-700 font-bold uppercase">
                      {r.category}
                    </span>
                  </td>
                  <td className="p-2">
                    <span className="px-2 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-800 font-bold">
                      {r.tier_gate}
                    </span>
                    {r.featured && (
                      <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-50 text-amber-800 font-bold">
                        FEAT
                      </span>
                    )}
                  </td>
                  <td className="p-2 text-right tabular-nums text-xs text-[var(--ink-2)]">{r.view_count || 0}</td>
                  <td className="p-2 text-center">
                    {r.published
                      ? <Eye size={14} className="text-emerald-600 inline" />
                      : <EyeOff size={14} className="text-slate-400 inline" />}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    <button type="button" className="text-xs px-2 py-1 hover:bg-slate-200 rounded" onClick={() => setEditing(r)} data-testid="admin-resource-edit">
                      <Edit3 size={12} className="inline" />
                    </button>
                    <button type="button" className="text-xs px-2 py-1 hover:bg-rose-100 text-rose-700 rounded" onClick={() => del(r.id, r.title)} data-testid="admin-resource-delete">
                      <Trash2 size={12} className="inline" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ResourceEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function ResourceEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;

  const onChange = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.slug || !form.title) {
      toast.error("Slug and title are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, tags: typeof form.tags === "string" ? form.tags.split(",").map((s) => s.trim()).filter(Boolean) : form.tags };
      if (isNew) {
        await api.post("/admin/resources", payload);
        toast.success("Resource created");
      } else {
        await api.patch(`/admin/resources/${form.id}`, payload);
        toast.success("Resource updated");
      }
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="admin-resource-editor">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div className="font-display font-extrabold text-xl">
            {isNew ? "New Resource" : `Edit · ${form.title}`}
          </div>
          <button type="button" onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
          <Field label="Title">
            <input type="text" value={form.title} onChange={(e) => onChange("title", e.target.value)} className="w-full px-3 py-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-title" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Slug (URL)">
              <input type="text" value={form.slug} onChange={(e) => onChange("slug", e.target.value)} className="w-full px-3 py-2 rounded border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-slug" />
            </Field>
            <Field label="Category">
              <select value={form.category} onChange={(e) => onChange("category", e.target.value)} className="w-full px-3 py-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-category">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Tier gate">
              <select value={form.tier_gate} onChange={(e) => onChange("tier_gate", e.target.value)} className="w-full px-3 py-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-tier-gate">
                {TIER_GATES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Summary (shown on cards)">
            <textarea value={form.summary} onChange={(e) => onChange("summary", e.target.value)} rows={2} className="w-full px-3 py-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-summary" />
          </Field>

          <Field label="Body (Markdown)">
            <textarea value={form.body_md} onChange={(e) => onChange("body_md", e.target.value)} rows={12} className="w-full px-3 py-2 rounded border border-slate-200 text-sm font-mono focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-body" />
          </Field>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="External URL (videos, vendor sites)">
              <input type="url" value={form.url} onChange={(e) => onChange("url", e.target.value)} className="w-full px-3 py-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-url" />
            </Field>
            <Field label="File URL (download)">
              <input type="url" value={form.file_url} onChange={(e) => onChange("file_url", e.target.value)} className="w-full px-3 py-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-file-url" />
            </Field>
          </div>

          <Field label="Tags (comma-separated)">
            <input type="text" value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags || ""} onChange={(e) => onChange("tags", e.target.value)} className="w-full px-3 py-2 rounded border border-slate-200 text-sm focus:outline-none focus:border-[var(--primary)]" data-testid="resource-form-tags" />
          </Field>

          <div className="flex items-center gap-4">
            <label className="text-sm inline-flex items-center gap-2">
              <input type="checkbox" checked={!!form.featured} onChange={(e) => onChange("featured", e.target.checked)} data-testid="resource-form-featured" />
              Featured
            </label>
            <label className="text-sm inline-flex items-center gap-2">
              <input type="checkbox" checked={form.published !== false} onChange={(e) => onChange("published", e.target.checked)} data-testid="resource-form-published" />
              Published
            </label>
          </div>
        </div>

        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving} data-testid="resource-form-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            {isNew ? "Create" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs text-[var(--ink-2)] uppercase tracking-wide mb-1">{label}</div>
      {children}
    </div>
  );
}
